import { AlertTriangle, Loader2 } from 'lucide-react';
import Notice from '../Notice';
import { tx } from '../../i18n';
import { num } from '../../lib/values';
import type { AreaReading } from '../../types/api';

interface AreaReadingPanelProps {
  reading: AreaReading[];
}

/** States that mean work is still happening, so the screen keeps asking. */
export const IN_PROGRESS = ['QUEUED', 'READING'];

/**
 * What is being read, while it is being read.
 *
 * <p>An area takes one call to the register per postcode, so the first look at a new outward code
 * is minutes rather than milliseconds. A spinner for that long says nothing except "wait"; a
 * count says how long is left and proves the thing is moving. The map fills in underneath as
 * rows land, so the wait is spent looking at a partial answer rather than at nothing.
 *
 * <p>The failures are named rather than spun on forever: no register token, an outward code the
 * postcode list does not hold, a register that stopped answering. What was read before a failure
 * is kept and shown - a partial area with its own coverage figure is a fact, not a broken page.
 */
export default function AreaReadingPanel({ reading }: AreaReadingPanelProps) {
  if (reading.length === 0) {
    return null;
  }

  const busy = reading.filter((row) => IN_PROGRESS.includes(row.state));
  const broken = reading.filter((row) =>
    ['FAILED', 'UNKNOWN', 'UNAVAILABLE', 'BUSY', 'LIMIT_REACHED', 'OUT_OF_AREA'].includes(row.state),
  );

  return (
    <>
      {busy.map((row) => {
        const known = num(row.postcodesKnown) ?? 0;
        const done = num(row.postcodesDone) ?? 0;
        const share = known > 0 ? Math.min(100, Math.round((done / known) * 100)) : 0;
        return (
          <div key={row.outcode} className="reading">
            <p className="reading__head">
              <Loader2 size={16} aria-hidden="true" className="reading__spin" />
              <span className="reading__title">
                {row.state === 'QUEUED'
                  ? tx(
                      `${row.outcode} is queued`,
                      `${row.outcode} sırada`,
                      `${row.outcode} w kolejce`,
                      `${row.outcode} está na fila`,
                      `${row.outcode} está en cola`,
                    )
                  : tx(
                      `Reading ${row.outcode}`,
                      `${row.outcode} okunuyor`,
                      `Odczytywanie ${row.outcode}`,
                      `A ler ${row.outcode}`,
                      `Leyendo ${row.outcode}`,
                    )}
              </span>
              <span className="small muted">
                {known > 0
                  ? tx(
                      `${done} of ${known} postcodes · ${row.certificates} certificates so far`,
                      `${known} posta kodundan ${done} tanesi · şu ana kadar ${row.certificates} sertifika`,
                      `${done} z ${known} kodów · ${row.certificates} świadectw jak dotąd`,
                      `${done} de ${known} códigos · ${row.certificates} certificados até agora`,
                      `${done} de ${known} códigos · ${row.certificates} certificados hasta ahora`,
                    )
                  : tx('Starting', 'Başlıyor', 'Rozpoczynanie', 'A começar', 'Empezando')}
              </span>
            </p>
            {/* A real progress bar: the denominator is the postcode list, not an animation. */}
            <div
              className="reading__track"
              role="progressbar"
              aria-valuenow={share}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={tx(
                `Reading ${row.outcode}`,
                `${row.outcode} okunuyor`,
                `Odczytywanie ${row.outcode}`,
                `A ler ${row.outcode}`,
                `Leyendo ${row.outcode}`,
              )}
            >
              <span className="reading__fill" style={{ width: `${share}%` }} />
            </div>
            <p className="small muted">
              {tx(
                'The map fills in as it goes; you can change the criteria while it runs. Read once, an area is immediate afterwards.',
                'Harita ilerledikçe doluyor; bu sürerken kriterleri değiştirebilirsiniz. Bir kez okunan bölge sonrasında anında geliyor.',
                'Mapa wypełnia się na bieżąco; możesz zmieniać kryteria w trakcie. Raz odczytany obszar jest potem natychmiastowy.',
                'O mapa vai-se preenchendo; pode mudar os critérios entretanto. Lida uma vez, a área é imediata depois.',
                'El mapa se va rellenando; puedes cambiar los criterios mientras tanto. Leída una vez, la zona es inmediata después.',
              )}
            </p>
          </div>
        );
      })}

      {broken.map((row) => (
        <Notice
          key={row.outcode}
          tone="warning"
          title={
            row.state === 'OUT_OF_AREA'
              ? tx(
                  `${row.outcode} is outside the area ScanHome covers`,
                  `${row.outcode} ScanHome'in kapsadığı alanın dışında`,
                  `${row.outcode} jest poza obszarem, który obejmuje ScanHome`,
                  `${row.outcode} está fora da área que o ScanHome cobre`,
                  `${row.outcode} está fuera del área que cubre ScanHome`,
                )
              : row.state === 'UNKNOWN'
              ? tx(
                  `${row.outcode} is not an outward code we hold`,
                  `${row.outcode} elimizdeki outcode'lardan değil`,
                  `${row.outcode} nie jest kodem, który posiadamy`,
                  `${row.outcode} não é um código que tenhamos`,
                  `${row.outcode} no es un código que tengamos`,
                )
              : row.state === 'UNAVAILABLE'
                ? tx(
                    'Reading areas is switched off',
                    'Bölge okuma kapalı',
                    'Odczytywanie obszarów jest wyłączone',
                    'A leitura de zonas está desligada',
                    'La lectura de zonas está desactivada',
                  )
                : row.state === 'LIMIT_REACHED'
                  ? tx(
                      'No more new areas can be read today',
                      'Bugün yeni bölge okunamıyor',
                      'Dziś nie można odczytać kolejnych obszarów',
                      'Não podem ser lidas mais áreas hoje',
                      'Hoy no se pueden leer más zonas',
                    )
                : row.state === 'BUSY'
                  ? tx(
                      'Too many areas are already being read',
                      'Aynı anda çok fazla bölge okunuyor',
                      'Zbyt wiele obszarów jest już odczytywanych',
                      'Já estão a ser lidas demasiadas zonas',
                      'Ya se están leyendo demasiadas zonas',
                    )
                  : tx(
                      `Reading ${row.outcode} stopped early`,
                      `${row.outcode} okuması yarıda kaldı`,
                      `Odczyt ${row.outcode} przerwany`,
                      `A leitura de ${row.outcode} parou`,
                      `La lectura de ${row.outcode} se detuvo`,
                    )
          }
        >
          <span className="reading__why">
            <AlertTriangle size={15} aria-hidden="true" />
            {row.state === 'OUT_OF_AREA'
              ? tx(
                  'The certificate and school registers this uses are England only for now.',
                  'Burada kullanılan sertifika ve okul kayıtları şimdilik yalnızca İngiltere’yi kapsıyor.',
                  'Rejestry świadectw i szkół, z których to korzysta, obejmują na razie tylko Anglię.',
                  'Os registos de certificados e de escolas usados aqui cobrem por agora apenas a Inglaterra.',
                  'Los registros de certificados y colegios que usa esto cubren por ahora solo Inglaterra.',
                )
              : row.state === 'UNKNOWN'
              ? tx(
                  'Check the code, or it may be one the postcode list does not cover.',
                  'Kodu kontrol edin; posta kodu listesinin kapsamadığı bir kod da olabilir.',
                  'Sprawdź kod - może to kod, którego lista pocztowa nie obejmuje.',
                  'Verifique o código; pode ser um que a lista de códigos não cobre.',
                  'Comprueba el código; puede ser uno que la lista de códigos no cubre.',
                )
              : row.state === 'UNAVAILABLE'
                ? tx(
                    'Only areas already read can be searched.',
                    'Yalnızca daha önce okunmuş bölgeler aranabilir.',
                    'Można przeszukiwać tylko obszary już odczytane.',
                    'Só podem ser pesquisadas zonas já lidas.',
                    'Solo se pueden buscar zonas ya leídas.',
                  )
                : row.state === 'LIMIT_REACHED'
                  ? tx(
                      'Areas already read are still searchable. This one can be read tomorrow.',
                      'Daha önce okunmuş bölgeler aranabilir durumda. Bu bölge yarın okunabilir.',
                      'Obszary już odczytane nadal można przeszukiwać. Ten można odczytać jutro.',
                      'As áreas já lidas continuam pesquisáveis. Esta pode ser lida amanhã.',
                      'Las zonas ya leídas siguen siendo buscables. Esta se podrá leer mañana.',
                    )
                : row.state === 'BUSY'
                  ? tx(
                      'Try again in a few minutes.',
                      'Birkaç dakika sonra tekrar deneyin.',
                      'Spróbuj za kilka minut.',
                      'Tente daqui a alguns minutos.',
                      'Inténtalo en unos minutos.',
                    )
                  : tx(
                      `${row.postcodesDone} of ${row.postcodesKnown} postcodes were read before the register stopped answering. They are in the result below; searching again continues from there.`,
                      `Kayıt sistemi yanıt vermeyi bırakmadan önce ${row.postcodesKnown} posta kodundan ${row.postcodesDone} tanesi okundu. Aşağıdaki sonuçta varlar; tekrar aradığınızda kaldığı yerden devam eder.`,
                      `Odczytano ${row.postcodesDone} z ${row.postcodesKnown} kodów, zanim rejestr przestał odpowiadać. Są w wyniku poniżej; kolejne wyszukiwanie kontynuuje od tego miejsca.`,
                      `Foram lidos ${row.postcodesDone} de ${row.postcodesKnown} códigos antes de o registo deixar de responder. Estão no resultado abaixo; pesquisar de novo continua daí.`,
                      `Se leyeron ${row.postcodesDone} de ${row.postcodesKnown} códigos antes de que el registro dejara de responder. Están en el resultado de abajo; buscar de nuevo continúa desde ahí.`,
                    )}
          </span>
        </Notice>
      ))}
    </>
  );
}
